import { SearchInputCommand } from "@/components/inputs/search-input-command";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useEffect } from "react";
import { debounce } from "@/utils/debounce";
import { Controller, useForm } from "react-hook-form";
import { useList } from "../../context/use-list";
import { FilterLicenseFeesFormData } from "./filters-license-fees.types";
import { useCompanySelection } from "@/hooks/useCompanySelection";
import { ComboboxDemo } from "@/components/ui/combobox";
import { getLicenseFeeStatusList } from "@/utils/get-license-fee-status";
import { ChevronDownIcon } from "lucide-react";

export const FiltersLicenseFees = () => {
  const { companies, handleSearchCompanies } = useCompanySelection({});
  const {
    isLoading,
    updateFilters,
    clearFilters: clearListFilters,
  } = useList();

  const { control, handleSubmit, reset, watch, setValue, getValues } =
    useForm<FilterLicenseFeesFormData>({
      mode: "onBlur",
      reValidateMode: "onChange",
      defaultValues: {
        company: "",
        startDate: "",
        endDate: "",
        status: {
          id: "",
          name: "",
        },
      },
    });

  const handleSelectCompany = (item: string) => {
    setValue("company", item);
  };

  const handleSelectPeriod = (
    nameKey: "startDate" | "endDate",
    item: string,
  ) => {
    setValue(nameKey, item);
  };

  const handleSelectStatus = (item: { id: number; name: string }) => {
    setValue("status", {
      id: item.id.toString(),
      name: item.name,
    });
  };

  const clearFilters = () => {
    reset({
      company: undefined,
      startDate: "",
      endDate: "",
      status: {
        id: "",
        name: "",
      },
    });
    clearListFilters();
  };

  useEffect(() => {
    const subscription = watch(() => {
      handleSubmit(onSubmit)();
    });
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch]);

  const onSubmit = (data: FilterLicenseFeesFormData) => {
    updateFilters({
      company: data.company,
      startDate: data.startDate,
      endDate: data.endDate,
      status: data.status?.id,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="bg-white px-8 pt-0 pb-4 mt-5 border-b">
        <div className="flex items-end justify-between">
          <div className="flex gap-5 flex-col w-full">
            <div className="flex gap-4 items-end w-full">
              <div className="w-1/4">
                <ComboboxDemo
                  key={`company-${!!getValues("company")}`}
                  name="company"
                  emptyPlaceholder="Empresa não encontrada"
                  searchPlaceholder="Busque uma empresa"
                  selectPlaceholder="Selecionar empresa"
                  onChange={debounce(handleSearchCompanies, 500)}
                  items={companies}
                  onSelect={handleSelectCompany}
                  shouldFilter={false}
                  defaultValue={getValues("company")}
                  customIcon={<ChevronDownIcon className={`h-4 w-4`} />}
                  minSearchLength={3}
                />
              </div>

              <div className="w-1/1">
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      id="start_date"
                      name="startDate"
                      type="date"
                      value={field.value}
                      onChange={(event) => {
                        field.onChange(event);
                        handleSelectPeriod("startDate", event.target.value);
                      }}
                      error={fieldState.error ? fieldState.error.message : ""}
                      required
                    />
                  )}
                />
              </div>

              <div className="w-1/1">
                <Controller
                  control={control}
                  name="endDate"
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      id="end_date"
                      name="endDate"
                      type="date"
                      value={field.value}
                      onChange={(event) => {
                        field.onChange(event);
                        handleSelectPeriod("endDate", event.target.value);
                      }}
                      error={fieldState.error ? fieldState.error.message : ""}
                      required
                    />
                  )}
                />
              </div>

              <div className="w-1/4">
                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <SearchInputCommand
                      {...field}
                      name="status"
                      chevron={true}
                      value={field.value?.name || ""}
                      searchDisable={true}
                      selectOption={(_, item) => {
                        if (!Array.isArray(item)) {
                          handleSelectStatus(item);
                        }
                      }}
                      onChange={(event) => {
                        field.onChange({ name: event.target.value });
                      }}
                      searchValue={field.value?.name || ""}
                      optionsList={getLicenseFeeStatusList()}
                      label="Selecionar status"
                      clear={!field.value?.id}
                      inputWidth="w-full"
                    />
                  )}
                />
              </div>
              <div className="gap-2 flex">
                <Button
                  disabled={isLoading}
                  variant="link"
                  type="button"
                  onClick={clearFilters}
                >
                  Limpar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};
